import { Outlet, useNavigation } from "react-router-dom";
import { useTranslation } from "@/core/i18n";

import { useFeatureFlag } from "services/Flags";

import { AccountNavRail } from "./AccountNavRail";
import { PendingRouteViewport } from "../PendingRouteViewport";
import { AccountDestinationSkeleton } from "./AccountDestinationSkeleton";

export const getPendingAccountTitle = (
  pathname: string | undefined,
  t: ReturnType<typeof useTranslation>["t"],
) => {
  if (pathname === "/account/details") return t("accountDetails.header");
  if (pathname?.includes("/orders")) return t("orderHistoryPage.header");
  if (pathname?.includes("/visits")) return t("visitHistory.header");
  if (pathname?.includes("/reward-history")) return t("rewardHistory.header");
  if (pathname?.includes("/assessments")) return t("assessmentHistory.header");
  if (pathname === "/account/privacy") return t("privacyChoices.title");
  if (pathname === "/platform-selector") return t("account.selectLocation");
  return t("account.header");
};

/**
 * Two-pane account area for tablet and desktop: menu on the left, the chosen
 * page on the right. Below `md` the rail is hidden and the account routes work
 * exactly as before — a list you drill into.
 */
export const AccountLayout = () => {
  const isDesktopExperience = useFeatureFlag("desktopExperience");
  const { t } = useTranslation();
  const navigation = useNavigation();
  const pendingAccountDestination =
    navigation.state === "loading" &&
    (navigation.location?.pathname.startsWith("/account/") ||
      navigation.location?.pathname === "/platform-selector");
  const pendingTitle = getPendingAccountTitle(navigation.location?.pathname, t);

  return (
    <div className="bg-one desk:mx-auto desk:grid desk:min-h-screen desk:max-w-screen-xl desk:grid-cols-[minmax(0,240px)_minmax(0,1fr)] desk:items-start desk:gap-x-10 desk:px-6 desk:py-10 desklg:gap-x-14 desklg:px-8 desk2xl:max-w-screen-2xl">
      {isDesktopExperience && (
        <AccountNavRail className="hidden desk:sticky desk:top-24 desk:flex" />
      )}
      <div className="desk:col-start-2 desk:min-w-0">
        {pendingAccountDestination ? (
          <PendingRouteViewport>
            <AccountDestinationSkeleton title={pendingTitle} />
          </PendingRouteViewport>
        ) : (
          <Outlet />
        )}
      </div>
    </div>
  );
};
